import { create } from 'zustand'
import type { ParentTicket } from '@/types/parentTicket'
import type { AgentOption, ClientOption, RequestOption } from '@/types/ticket'

const daysFromNow = (days: number) =>
  new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString()

/** Field engineer summary from API (primary/backup engineer details) */
export type FieldEngineerSummary = {
  id: number
  full_name?: string | null
  user_id?: number | null
  user_login?: string | null
  user_email?: string | null
  cluster?: string | null
  province?: string | null
  region?: string | null
  address?: string | null
  launch_point?: string | null
  /** Certification names (e.g. ['LENOVO']) */
  certifications?: string[]
  /** Active requests count for display */
  workload?: number
  /** Match reasons for display (e.g. ['assigned', 'low_workload']) */
  match_reasons?: string[]
}

/** Ticket with optional fields used by the form (from GET /tickets/:id) */
export type ParentTicketWithMeta = ParentTicket & {
  store_id?: number
  store_code?: string
  request?: RequestOption
  store?: ClientOption
  agent?: AgentOption
  primaryEngineer?: FieldEngineerSummary
  backupEngineer?: FieldEngineerSummary
}

type ParentTicketState = {
  tickets: ParentTicket[]
  setTickets: (tickets: ParentTicket[]) => void
  /** Add or replace a single ticket (e.g. after fetching GET /tickets/:id) */
  mergeTicket: (ticket: ParentTicketWithMeta) => void

  getTicketById: (id: string) => ParentTicket | undefined
  updateTicket: (id: string, patch: Partial<ParentTicket>) => void
  updateTicketStatus: (id: string, status: ParentTicket['status']) => void
}

export const useParentTicketStore = create<ParentTicketState>((set, get) => ({
  tickets: [
    {
      id: 'T000001',
      requestId: 'R000001',
      clientName: 'Alberto Ripley',
      clientOrg: 'SM Investments',
      agentName: 'James Lourdes',
      requestedAt: '2025-04-30T09:30:00.000Z',
      ageingLabel: '00:12',
      severity: 'Low',
      slaDueAt: daysFromNow(5),
      sla: 'Within SLA',
      status: 'Open',
      description:
        'POS terminal freezes during end-of-day batch. Started after recent Windows update. Client requests onsite diagnosis and possible rollback.POS terminal freezes during end-of-day batch. Started after recent Windows update. Client requests onsite diagnosis and possible rollback.POS terminal freezes during end-of-day batch. Started after recent Windows update. Client requests onsite diagnosis and possible rollback.POS terminal freezes during end-of-day batch. Started after recent Windows update. Client requests onsite diagnosis and possible rollback.',
    },
    {
      id: 'T000002',
      requestId: 'R000002',
      clientName: 'Susan Babin',
      clientOrg: 'Ayala Corporation',
      agentName: null,
      requestedAt: '2025-04-15T11:20:00.000Z',
      ageingLabel: '72:45',
      severity: 'Mid',
      slaDueAt: daysFromNow(4),
      sla: 'Within SLA',
      status: 'On Hold - Waiting On Customer',
      description:
        'Customer reported intermittent disconnections. Waiting for store to provide photos of cabling and router status lights.',
    },
    {
      id: 'T000003',
      requestId: 'R000010',
      clientName: 'Grace Villanueva',
      clientOrg: 'SM Investments',
      agentName: 'Ivy Santos',
      requestedAt: '2025-04-20T13:45:00.000Z',
      ageingLabel: '15:20',
      severity: 'High',
      slaDueAt: daysFromNow(3),
      sla: 'Overdue',
      status: 'Pending - Parts Replacement',
      description:
        'Printer output is faded and paper jams frequently. Replacement parts requested; pending vendor delivery and confirmation.',
    },
    {
      id: 'T000004',
      requestId: 'R000011',
      clientName: 'Daniel Chua',
      clientOrg: 'Globe Telecom',
      agentName: 'Carlos Tan',
      requestedAt: '2025-04-18T08:15:00.000Z',
      ageingLabel: '48:05',
      severity: 'Urgent',
      slaDueAt: daysFromNow(6),
      sla: 'Breach',
      status: 'On Hold - Waiting On Parts',
      description:
        'POS will not power on. Suspected power supply failure. Unit is down; awaiting parts replacement to proceed.',
    },
    {
      id: 'T000005',
      requestId: 'R000012',
      clientName: 'Hannah Lim',
      clientOrg: 'UnionBank',
      agentName: null,
      requestedAt: '2025-04-28T10:10:00.000Z',
      ageingLabel: '08:40',
      severity: 'Mid',
      slaDueAt: daysFromNow(2),
      sla: 'Within SLA',
      status: 'Open',
      description:
        'User cannot login to backoffice dashboard. Error appears after password reset attempt. Needs account verification.',
    },
    {
      id: 'T000006',
      requestId: 'R000013',
      clientName: 'Rico Mendoza',
      clientOrg: 'Metrobank',
      agentName: 'Jamie Cruz',
      requestedAt: '2025-04-12T09:05:00.000Z',
      ageingLabel: '96:30',
      severity: 'High',
      slaDueAt: daysFromNow(3),
      sla: 'Overdue',
      status: 'On Hold - Waiting On Parts',
      description:
        'Receipt printer reports cutter error. Multiple restarts attempted. Waiting on replacement cutter module.',
    },
    {
      id: 'T000007',
      requestId: 'R000014',
      clientName: 'Ivan Bautista',
      clientOrg: 'AboitizPower',
      agentName: null,
      requestedAt: '2025-04-22T14:55:00.000Z',
      ageingLabel: '26:15',
      severity: 'Low',
      slaDueAt: daysFromNow(4),
      sla: 'Within SLA',
      status: 'Resolved-Incomplete',
      description:
        'Issue resolved on admin side but client did not confirm. Marked resolved-incomplete pending acknowledgement.',
    },
    {
      id: 'T000008',
      requestId: 'R000015',
      clientName: 'Bianca Torres',
      clientOrg: 'RCBC',
      agentName: 'Eric Santos',
      requestedAt: '2025-04-25T07:35:00.000Z',
      ageingLabel: '32:05',
      severity: 'Medium',
      slaDueAt: daysFromNow(2),
      sla: 'Within SLA',
      status: 'On Hold - Waiting On Customer',
      description:
        'Remote troubleshooting done. Waiting for customer to send updated screenshots and logs for validation.',
    },
    {
      id: 'T000009',
      requestId: 'R000016',
      clientName: 'Leah Ramos',
      clientOrg: 'ACEN',
      agentName: 'Miguel Santos',
      requestedAt: '2025-04-27T16:20:00.000Z',
      ageingLabel: '18:30',
      severity: 'Urgent',
      slaDueAt: daysFromNow(5),
      sla: 'Overdue',
      status: 'Pending - Parts Replacement',
      description:
        'Cash drawer not opening via POS trigger. Suspected solenoid issue. Parts request filed; awaiting replacement.',
    },
    {
      id: 'T000010',
      requestId: 'R000017',
      clientName: 'Omar Navarro',
      clientOrg: 'Cebu Pacific',
      agentName: null,
      requestedAt: '2025-04-19T12:05:00.000Z',
      ageingLabel: '60:10',
      severity: 'Low',
      slaDueAt: null,
      sla: 'Within SLA',
      status: 'Closed',
      description:
        'Ticket closed after successful remote guidance. No further issues reported by client.',
    },
    {
      id: 'T000011',
      requestId: 'R000022',
      clientName: 'Tina Delgado',
      clientOrg: 'Petron',
      agentName: 'Arvin Cruz',
      requestedAt: '2025-04-23T18:40:00.000Z',
      ageingLabel: '28:50',
      severity: 'Mid',
      slaDueAt: null,
      sla: 'N/A',
      status: 'Cancelled',
      description:
        'Client cancelled request before troubleshooting. Duplicate ticket created by mistake.',
    },
    {
      id: 'T000012',
      requestId: 'R000019',
      clientName: 'Noel Garcia',
      clientOrg: 'PLDT',
      agentName: 'Marie Abad',
      requestedAt: '2025-04-21T09:55:00.000Z',
      ageingLabel: '40:25',
      severity: 'High',
      slaDueAt: daysFromNow(1),
      sla: 'Within SLA',
      status: 'Resolved-Incomplete',
      description:
        'Fix applied; awaiting client validation. Marked resolved-incomplete until confirmation is received.',
    },
  ],

  setTickets: (tickets) => set({ tickets }),

  mergeTicket: (ticket) =>
    set((state) => ({
      tickets: state.tickets
        .filter((t) => String(t.id) !== String(ticket.id))
        .concat([ticket]),
    })),

  getTicketById: (id) => get().tickets.find((t) => String(t.id) === String(id)),

  updateTicket: (id, patch) =>
    set((state) => ({
      tickets: state.tickets.map((t) => (t.id === id ? { ...t, ...patch } : t)),
    })),

  updateTicketStatus: (id, status) => {
    get().updateTicket(id, { status })
  },
}))
